import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Check, Crown, Minus, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

type Tier = {
  id: string;
  slug: string;
  name: string;
  tagline: string | null;
  monthly_price: number;
  perks: string[];
  can_book_without_payment: boolean;
  display_order: number;
};

// Every distinct perk becomes a row in the matrix table; a tier "has" a perk
// if the string appears in its `perks` array (case-insensitive match).
function buildMatrix(tiers: Tier[]) {
  const rows: string[] = [];
  const seen = new Set<string>();
  for (const t of tiers) {
    for (const p of t.perks ?? []) {
      const k = p.trim().toLowerCase();
      if (!seen.has(k)) {
        seen.add(k);
        rows.push(p.trim());
      }
    }
  }
  return rows;
}
function tierHas(t: Tier, perk: string) {
  const k = perk.trim().toLowerCase();
  return (t.perks ?? []).some((p) => p.trim().toLowerCase() === k);
}

const Membership = () => {
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [currentTier, setCurrentTier] = useState<string | null>(null);
  const [authed, setAuthed] = useState(false);

  useEffect(() => {
    supabase
      .from("membership_tiers" as any)
      .select("*")
      .eq("active", true)
      .order("display_order")
      .then(({ data }) => setTiers((data as any) ?? []));
    supabase.auth.getSession().then(({ data: s }) => {
      const uid = s?.session?.user?.id;
      setAuthed(!!uid);
      if (!uid) return;
      supabase
        .from("customer_memberships" as any)
        .select("tier_slug,status")
        .eq("user_id", uid)
        .maybeSingle()
        .then(({ data }) => {
          if ((data as any)?.status === "active") setCurrentTier((data as any).tier_slug);
        });
    });
  }, []);

  const matrix = buildMatrix(tiers);
  const currentIdx = tiers.findIndex((t) => t.slug === currentTier);

  return (
    <div className="pt-24 min-h-screen">
      <Helmet>
        <title>Membership Tiers | MyStudio Columbus, GA</title>
        <meta name="description" content="Choose your MyStudio membership: Frame, Aperture, or Atelier. Top-tier Atelier members can book sessions without paying upfront." />
        <link rel="canonical" href="https://bookmystudiospace.com/membership" />
      </Helmet>
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-12">
            <p className="text-xs font-mono uppercase tracking-[0.3em] text-primary mb-3">Membership</p>
            <h1 className="text-4xl md:text-6xl font-display tracking-widest mb-4">CHOOSE YOUR TIER</h1>
            <p className="text-muted-foreground font-body max-w-2xl mx-auto">
              Three levels for every creative. Atelier members can hold studio time without paying upfront.
            </p>
            {currentTier && (
              <p className="mt-4 text-xs font-mono uppercase tracking-widest text-primary">
                You're on{" "}
                <span className="text-foreground">
                  {tiers.find((t) => t.slug === currentTier)?.name}
                </span>
                .
              </p>
            )}
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {tiers.map((t) => {
              const isCurrent = currentTier === t.slug;
              const isTop = t.can_book_without_payment;
              const idx = tiers.indexOf(t);
              const isUpgrade = currentIdx >= 0 && idx > currentIdx;
              return (
                <div
                  key={t.id}
                  className={`relative bg-panel border p-7 flex flex-col ${
                    isCurrent
                      ? "border-primary"
                      : isTop
                      ? "border-primary/60"
                      : "border-border"
                  }`}
                >
                  {isTop && !isCurrent && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-[10px] font-mono uppercase tracking-widest px-3 py-1 flex items-center gap-1">
                      <Crown className="w-3 h-3" /> Top tier
                    </div>
                  )}
                  {isCurrent && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-foreground text-background text-[10px] font-mono uppercase tracking-widest px-3 py-1">
                      Current plan
                    </div>
                  )}
                  <h2 className="font-display tracking-widest text-2xl mb-1">{t.name.toUpperCase()}</h2>
                  {t.tagline && (
                    <p className="text-sm text-muted-foreground font-body mb-5">{t.tagline}</p>
                  )}
                  <div className="mb-6">
                    <span className="font-display text-4xl text-primary">
                      ${Number(t.monthly_price).toFixed(0)}
                    </span>
                    <span className="text-sm font-mono text-muted-foreground ml-1">/mo</span>
                  </div>
                  {isTop && (
                    <div className="mb-5 flex items-center gap-2 border border-primary/40 bg-primary/5 px-3 py-2 text-[11px] font-mono uppercase tracking-widest text-primary">
                      <Clock className="w-3 h-3" /> Pay-later on bookings
                    </div>
                  )}
                  <ul className="space-y-2 mb-7 flex-1">
                    {(t.perks ?? []).map((p: string, i: number) => (
                      <li key={i} className="flex items-start gap-2 text-sm font-body">
                        <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                  {isCurrent ? (
                    <Link to="/account">
                      <Button variant="outline" className="w-full font-display tracking-widest">
                        Manage
                      </Button>
                    </Link>
                  ) : t.monthly_price > 0 ? (
                    <Link to="/contact?subject=Membership%20upgrade">
                      <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-display tracking-widest">
                        {isUpgrade ? `Upgrade to ${t.name}` : `Request ${t.name}`}
                      </Button>
                    </Link>
                  ) : (
                    <Link to={authed ? "/account" : "/signup"}>
                      <Button variant="outline" className="w-full font-display tracking-widest">
                        {authed ? "Manage" : "Get started"}
                      </Button>
                    </Link>
                  )}
                </div>
              );
            })}
          </div>

          {/* Perks comparison matrix */}
          {tiers.length > 0 && matrix.length > 0 && (
            <div className="mt-16">
              <h2 className="font-display tracking-widest text-xl md:text-2xl text-center mb-6">
                COMPARE PERKS
              </h2>
              <div className="bg-panel border border-border overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left p-4 font-mono text-xs uppercase tracking-widest text-muted-foreground">
                        Perk
                      </th>
                      {tiers.map((t) => (
                        <th
                          key={t.id}
                          className={`p-4 text-center font-display tracking-widest text-xs uppercase ${
                            currentTier === t.slug ? "text-primary" : ""
                          }`}
                        >
                          {t.name}
                        </th>
                      ))}
                    </tr>
                    <tr className="border-b border-border">
                      <th className="text-left p-4 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                        Pay later on bookings
                      </th>
                      {tiers.map((t) => (
                        <td key={t.id} className="p-4 text-center">
                          {t.can_book_without_payment ? (
                            <Check className="w-4 h-4 text-primary mx-auto" />
                          ) : (
                            <Minus className="w-4 h-4 text-muted-foreground/40 mx-auto" />
                          )}
                        </td>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {matrix.map((perk) => (
                      <tr key={perk} className="border-b border-border last:border-b-0">
                        <td className="p-4 font-body text-sm">{perk}</td>
                        {tiers.map((t) => (
                          <td key={t.id} className="p-4 text-center">
                            {tierHas(t, perk) ? (
                              <Check className="w-4 h-4 text-primary mx-auto" />
                            ) : (
                              <Minus className="w-4 h-4 text-muted-foreground/40 mx-auto" />
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <p className="text-center text-xs font-mono text-muted-foreground mt-10">
            Memberships are managed manually. Contact us to upgrade or change tiers.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Membership;